
import React, { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

interface ProtectedRouteProps {
  redirectPath?: string;
  children?: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute = ({
  redirectPath = "/auth",
  children,
  requireAdmin = false
}: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // Debug authentication state
  useEffect(() => {
    console.log("ProtectedRoute - auth state:", { 
      user: user?.id, 
      email: user?.email,
      loading, 
      path: location.pathname,
      requireAdmin 
    });
  }, [user, loading, location, requireAdmin]);
  
  // Wait for auth to initialize before redirecting
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FF8C00] border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    // Save the current location to redirect back after login
    return <Navigate to={redirectPath} state={{ from: location.pathname }} replace />;
  }

  // Check admin access if required - hassaanabegam@gmail.com is always admin
  if (requireAdmin && user.email !== 'hassaanabegam@gmail.com') {
    return <Navigate to="/dashboard" replace />;
  }

  // If user is logged in but on auth page, redirect to dashboard
  if (user && location.pathname === '/auth') {
    return <Navigate to="/dashboard" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
